import {
    CreatePostDto,
    Post,
    PostListResponse,
    PostLikeResponse
} from '@shared/schemas/posts';
import {
    Notification,
    NotificationListResponse,
    MarkReadResponse,
} from '@shared/schemas/notifications';
import {
    Message,
    MessageListResponse,
    CreateMessageDto,
    CreateMessageResponse,
} from '@shared/schemas/messages';
import {
    CreateConversationDto,
    CreateConversationResponse,
    RenameConversationDto,
    UpdateConversationThemeDto,
    ThemeResponse,
    Conversation,
} from '@shared/schemas/conversations';
import {
    RegisterDto,
    LoginDto,
    RegisterResponse,
    LoginResponse,
    AuthUser,
} from '@shared/schemas/auth';
import {
    SiteAdminsResponse,
    SiteAdminActionDto,
    AdminActionResponse,
    UpdateCommunityThemeDto,
} from '@shared/schemas/admin';
import {
    SettingsResponse,
    UpdateSettingsDto,
} from '@shared/schemas/settings';
import {
    Community,
    CommunityListResponse,
    CreateCommunityDto,
    CreateCommunityResponse,
    JoinCommunityDto,
    JoinResponse,
    LeaveResponse,
    CreateInvitationDto,
    CreateInvitationResponse,
    InvitationListResponse,
    InvitationDetails,
    AcceptInvitationResponse,
    JoinRequestListResponse,
    ProcessJoinRequestDto,
    ProcessJoinRequestResponse,
    JoinRequestDetails,
    CreateCommunityPostDto,
    CommunityPost,
    ToggleLikeResponse,
    CreateThreadDto,
    Thread,
    CreateChatMessageDto,
    ChatMessage,
    ChatMessageListResponse,
    CreateThreadReplyDto,
    ThreadReply,
    ThreadReplyListResponse,
    CreatePostReplyDto,
    PostReply,
    PostReplyListResponse,
    ReplyLikeResponse,
} from '@shared/schemas/communities';
import {
    UpdateProfileDto,
    ProfileResponse,
    UpdateProfileResponse,
    PinPostDto,
    PinPostResponse,
    CreateAdminPostDto,
    AdminPostResponse,
    SetProfileThemeDto,
    SetThemeResponse,
} from '@shared/schemas/profile';
import { fetchWithAuth } from './fetchWithAuth';
import {
    initializeAuth,
    clearAuth,
} from './auth-fetch.js';

const isProd = import.meta.env.PROD;
const API_URL = import.meta.env.VITE_API_URL || (isProd
  ? "https://social.earthservers.net/api"  // Must have /api for Apache proxy
  : "http://localhost:3001/api");

if (!API_URL) {
  throw new Error('VITE_API_URL environment variable is not defined');
}

/**
 * Posts
 */
export async function getPosts(): Promise<Post[]> {
    const res = await fetchWithAuth(`${API_URL}/posts`, {
        credentials: 'include',
    });

    if (!res.ok) {
        throw new Error('Failed to fetch posts');
    }

    const data: PostListResponse = await res.json();
    return data.items;
}

export async function createPost(data: CreatePostDto): Promise<Post> {
    const res = await fetchWithAuth(`${API_URL}/posts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(data),
    });

    if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to create post');
    }

    const result = await res.json();
    return result.post;
}

export async function togglePostLike(postId: number): Promise<PostLikeResponse> {
    const res = await fetchWithAuth(`${API_URL}/posts/${postId}/like`, {
        method: 'POST',
        credentials: 'include',
    });

    if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to toggle like');
    }

    return res.json();
}

export async function adminPinPost(postId: number, pinned: boolean): Promise<any> {
    const res = await fetchWithAuth(`${API_URL}/posts/${postId}/admin-pin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ adminPinned: pinned }),
    });

    if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to toggle admin pin');
    }

    return res.json();
}

/**
 * Get user's notifications
 */
export async function getNotifications(limit = 20): Promise<NotificationListResponse> {
    const res = await fetchWithAuth(`${API_URL}/notifications?limit=${limit}`, {
        credentials: 'include',
    });

    if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to fetch notifications');
    }

    return res.json();
}

/**
 * Mark a notification as read
 */
export async function markNotificationAsRead(
    notificationId: string
): Promise<MarkReadResponse> {
    const res = await fetchWithAuth(`${API_URL}/notifications/${notificationId}/read`, {
        method: 'POST',
        credentials: 'include',
    });

    if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to mark notification as read');
    }

    return res.json();
}

/**
 * Mark all notifications as read
 */
export async function markAllNotificationsAsRead(): Promise<MarkReadResponse> {
    const res = await fetchWithAuth(`${API_URL}/notifications/read-all`, {
        method: 'POST',
        credentials: 'include',
    });

    if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to mark all notifications as read');
    }

    return res.json();
}

/**
 * Delete a notification
 */
export async function deleteNotification(
    notificationId: string
): Promise<MarkReadResponse> {
    const res = await fetchWithAuth(`${API_URL}/notifications/${notificationId}`, {
        method: 'DELETE',
        credentials: 'include',
    });

    if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to delete notification');
    }

    return res.json();
}

/**
 * Clear (delete) all notifications
 */
export async function clearAllNotifications(): Promise<{ success: boolean }> {
    const res = await fetchWithAuth(`${API_URL}/notifications/clear-all`, {
        method: 'DELETE',
        credentials: 'include',
    });

    if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to clear notifications');
    }

    return res.json();
}

// ===== MESSAGES =====
export async function getMessages(conversationId: string): Promise<Message[]> {
    const res = await fetchWithAuth(
        `${API_URL}/messages?conversationId=${conversationId}`,
        { credentials: 'include' }
    );

    if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to fetch messages');
    }

    const data: MessageListResponse = await res.json();
    return data.messages;
}

export async function sendMessage(data: CreateMessageDto): Promise<Message> {
    const res = await fetchWithAuth(`${API_URL}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(data),
    });

    if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to send message');
    }

    const result: CreateMessageResponse = await res.json();
    return result.message;
}

// ===== CONVERSATIONS =====
export async function getConversations(): Promise<Conversation[]> {
    const res = await fetchWithAuth(`${API_URL}/conversations`, {
        credentials: 'include',
    });

    if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to fetch conversations');
    }

    const data = await res.json();
    return Array.isArray(data) ? data : data.conversations || [];
}

export async function getConversation(conversationId: string): Promise<Conversation> {
    const res = await fetchWithAuth(`${API_URL}/conversations/${conversationId}`, {
        credentials: 'include',
    });

    if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to fetch conversation');
    }

    const data = await res.json();
    return data.conversation;
}

export async function createConversation(
    data: CreateConversationDto
): Promise<CreateConversationResponse> {
    const res = await fetchWithAuth(`${API_URL}/conversations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(data),
    });

    if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to create conversation');
    }

    return res.json();
}

export async function renameConversation(
    conversationId: string,
    name: string
): Promise<{ id: string; name: string | null }> {
    const res = await fetchWithAuth(`${API_URL}/conversations/${conversationId}/name`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ name }),
    });

    if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to rename conversation');
    }

    return res.json();
}

export async function updateConversationTheme(
    conversationId: string,
    data: UpdateConversationThemeDto
): Promise<ThemeResponse> {
    const res = await fetchWithAuth(`${API_URL}/conversations/${conversationId}/theme`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(data),
    });

    if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to update theme');
    }

    return res.json();
}

// ====== Health ======
export async function checkHealth() {
    const res = await fetchWithAuth(`${API_URL}/health`);

    if (!res.ok) {
        throw new Error('Health check failed');
    }

    return res.json();
}

export async function checkDetailedHealth() {
    const res = await fetchWithAuth(`${API_URL}/health`);

    if (!res.ok) {
        throw new Error('Detailed health check failed');
    }

    return res.json();
}

// ===== AUTH =====
export async function register(data: RegisterDto): Promise<RegisterResponse> {
    const res = await fetchWithAuth(`${API_URL}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(data),
    });

    if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Registration failed');
    }

    return res.json();
}

export async function login(data: LoginDto): Promise<LoginResponse> {
    const res = await fetchWithAuth(`${API_URL}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(data),
    });

    if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Login failed');
    }

    const result = await res.json();

    // Initialize auth with new access token
    if (result.accessToken) {
        initializeAuth({ accessToken: result.accessToken });
    }

    return result;
}

export async function logout(): Promise<void> {
    const res = await fetchWithAuth(`${API_URL}/auth/logout`, {
        method: 'POST',
        credentials: 'include',
    });

    // Clear auth state regardless of response
    clearAuth();

    if (!res.ok) {
        throw new Error('Logout failed');
    }
}

export async function getCurrentUser(): Promise<AuthUser> {
    const res = await fetchWithAuth(`${API_URL}/auth/me`, {
        credentials: 'include',
    });

    if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Not authenticated');
    }

    const data = await res.json();
    return data.user;
}

// ===== ADMIN =====
export async function getSiteAdmins(): Promise<SiteAdminsResponse> {
    const res = await fetchWithAuth(`${API_URL}/admin/site-admins`, {
        credentials: 'include',
    });

    if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to fetch site admins');
    }

    return res.json();
}

export async function manageSiteAdmin(
    data: SiteAdminActionDto
): Promise<AdminActionResponse> {
    const res = await fetchWithAuth(`${API_URL}/admin/site-admins`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(data),
    });

    if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to manage admin privileges');
    }

    return res.json();
}

export async function updateCommunityTheme(
    communityId: string,
    themeId: string | null
): Promise<any> {
    const res = await fetchWithAuth(`${API_URL}/admin/community/${communityId}/theme`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ themeId }),
    });

    if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to update community theme');
    }

    return res.json();
}

// ===== SETTINGS =====
export async function getSettings(): Promise<SettingsResponse> {
    const res = await fetchWithAuth(`${API_URL}/settings`, {
        credentials: 'include',
    });

    if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to fetch settings');
    }

    return res.json();
}

export async function updateSettings(
    data: UpdateSettingsDto
): Promise<{ success: boolean }> {
    const res = await fetchWithAuth(`${API_URL}/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(data),
    });

    if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to update settings');
    }

    return res.json();
}

export async function getAccountInfo(): Promise<{ email: string; username: string; createdAt: string; postCount: number }> {
    const res = await fetchWithAuth(`${API_URL}/settings/account-info`, {
        credentials: 'include',
    });
    if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to fetch account info');
    }
    return res.json();
}

export async function changePassword(data: { currentPassword: string; newPassword: string; confirmPassword: string }): Promise<{ success: boolean }> {
    const res = await fetchWithAuth(`${API_URL}/settings/change-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(data),
    });
    if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to change password');
    }
    return res.json();
}

export async function changeEmail(data: { newEmail: string; password: string }): Promise<{ success: boolean; email: string }> {
    const res = await fetchWithAuth(`${API_URL}/settings/change-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(data),
    });
    if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to change email');
    }
    return res.json();
}

export async function changeUsername(data: { newUsername: string; password: string }): Promise<{ success: boolean; username: string }> {
    const res = await fetchWithAuth(`${API_URL}/settings/change-username`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(data),
    });
    if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to change username');
    }
    return res.json();
}

export async function checkUsernameAvailability(username: string): Promise<{ available: boolean }> {
    const res = await fetchWithAuth(`${API_URL}/settings/check-username/${encodeURIComponent(username)}`, {
        credentials: 'include',
    });
    if (!res.ok) throw new Error('Failed to check username');
    return res.json();
}

export async function deleteAccount(data: { password: string; confirmation: string }): Promise<{ success: boolean }> {
    const res = await fetchWithAuth(`${API_URL}/settings/account`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(data),
    });
    if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to delete account');
    }
    return res.json();
}

export async function exportUserData(): Promise<Blob> {
    const res = await fetchWithAuth(`${API_URL}/settings/export-data`, {
        credentials: 'include',
    });
    if (!res.ok) throw new Error('Failed to export data');
    return res.blob();
}

export async function getDataUsageStats(): Promise<{
    postCount: number;
    commentCount: number;
    communityCount: number;
    messageCount: number;
    followerCount: number;
    followingCount: number;
    accountAge: string;
}> {
    const res = await fetchWithAuth(`${API_URL}/settings/data-stats`, {
        credentials: 'include',
    });
    if (!res.ok) throw new Error('Failed to fetch data stats');
    return res.json();
}

// ===== COMMUNITIES =====

/**
 * Get all communities
 */
export async function getCommunities(): Promise<Community[]> {
    const res = await fetchWithAuth(`${API_URL}/communities`, {
        credentials: 'include',
    });

    if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to fetch communities');
    }

    const data: CommunityListResponse = await res.json();
    return data.communities;
}

/**
 * Get community feed - posts from joined communities
 */
export async function getCommunityFeed(): Promise<any[]> {
    const res = await fetchWithAuth(`${API_URL}/communities/feed`, {
        credentials: 'include',
    });

    if (!res.ok) return [];

    const data = await res.json();
    return data.posts || [];
}

/**
 * Create a new community
 */
export async function createCommunity(data: CreateCommunityDto): Promise<Community> {
    const res = await fetchWithAuth(`${API_URL}/communities`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(data),
    });

    if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to create community');
    }

    const result: CreateCommunityResponse = await res.json();
    return result.community;
}

/**
 * Join a community
 */
export async function joinCommunity(
    communityId: string,
    data: JoinCommunityDto
): Promise<JoinResponse> {
    const res = await fetchWithAuth(`${API_URL}/communities/${communityId}/join`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(data),
    });

    if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to join community');
    }

    return res.json();
}

/**
 * Leave a community
 */
export async function leaveCommunity(communityId: string): Promise<LeaveResponse> {
    const res = await fetchWithAuth(`${API_URL}/communities/${communityId}/join`, {
        method: 'DELETE',
        credentials: 'include',
    });

    if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to leave community');
    }

    return res.json();
}

/**
 * Create a community invitation
 */
export async function createCommunityInvitation(
    communityId: string,
    data: CreateInvitationDto
): Promise<CreateInvitationResponse> {
    const res = await fetchWithAuth(`${API_URL}/communities/${communityId}/invitations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(data),
    });

    if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to create invitation');
    }

    return res.json();
}

/**
 * Get invitations for a community
 */
export async function getCommunityInvitations(
    communityId: string
): Promise<InvitationListResponse> {
    const res = await fetchWithAuth(`${API_URL}/communities/${communityId}/invitations`, {
        credentials: 'include',
    });

    if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to fetch invitations');
    }

    return res.json();
}

/**
 * Get invitation details by token
 */
export async function getInvitationDetails(
    communityId: string,
    token: string
): Promise<InvitationDetails> {
    const res = await fetchWithAuth(`${API_URL}/communities/${communityId}/invitations/${token}`, {
        credentials: 'include',
    });

    if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to fetch invitation details');
    }

    return res.json();
}

/**
 * Accept a community invitation
 */
export async function acceptCommunityInvitation(
    communityId: string,
    token: string
): Promise<AcceptInvitationResponse> {
    const res = await fetchWithAuth(`${API_URL}/communities/${communityId}/invitations/${token}/accept`, {
        method: 'POST',
        credentials: 'include',
    });

    if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to accept invitation');
    }

    return res.json();
}

/**
 * Get join requests for a community
 */
export async function getCommunityJoinRequests(
    communityId: string,
    status?: 'pending' | 'approved' | 'denied'
): Promise<JoinRequestListResponse> {
    const url = new URL(`${API_URL}/communities/${communityId}/requests`);
    if (status) {
        url.searchParams.set('status', status);
    }

    const res = await fetchWithAuth(url.toString(), {
        credentials: 'include',
    });

    if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to fetch join requests');
    }

    return res.json();
}

/**
 * Get a specific join request
 */
export async function getCommunityJoinRequest(
    communityId: string,
    requestId: string
): Promise<JoinRequestDetails> {
    const res = await fetchWithAuth(`${API_URL}/communities/${communityId}/requests/${requestId}`, {
        credentials: 'include',
    });

    if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to fetch join request');
    }

    return res.json();
}

/**
 * Process a join request (approve/deny)
 */
export async function processCommunityJoinRequest(
    communityId: string,
    requestId: string,
    data: ProcessJoinRequestDto
): Promise<ProcessJoinRequestResponse> {
    const res = await fetchWithAuth(`${API_URL}/communities/${communityId}/requests/${requestId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(data),
    });

    if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to process join request');
    }

    return res.json();
}

/**
 * Create a post in a community
 */
export async function createCommunityPost(
    communityId: string,
    data: CreateCommunityPostDto
): Promise<CommunityPost> {
    const res = await fetchWithAuth(`${API_URL}/communities/${communityId}/posts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(data),
    });

    if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to create community post');
    }

    const result = await res.json();
    return result.post;
}

/**
 * Toggle like on a community post
 */
export async function toggleCommunityPostLike(
    communityId: string,
    postId: string
): Promise<ToggleLikeResponse> {
    const res = await fetchWithAuth(`${API_URL}/communities/${communityId}/posts/${postId}/like`, {
        method: 'POST',
        credentials: 'include',
    });

    if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to toggle like');
    }

    return res.json();
}

/**
 * Create a reply to a community post
 */
export async function createCommunityPostReply(
    communityId: string,
    postId: string,
    data: CreatePostReplyDto
): Promise<PostReply> {
    const res = await fetchWithAuth(`${API_URL}/communities/${communityId}/posts/${postId}/replies`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(data),
    });

    if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to create reply');
    }

    const result = await res.json();
    return result.reply;
}

/**
 * Get replies for a community post
 */
export async function getCommunityPostReplies(
    communityId: string,
    postId: string
): Promise<PostReply[]> {
    const res = await fetchWithAuth(`${API_URL}/communities/${communityId}/posts/${postId}/replies`, {
        credentials: 'include',
    });

    if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to fetch post replies');
    }

    const data: PostReplyListResponse = await res.json();
    return data.replies;
}

/**
 * Toggle like on a post reply
 */
export async function toggleCommunityPostReplyLike(
    communityId: string,
    postId: string,
    replyId: string
): Promise<ReplyLikeResponse> {
    const res = await fetchWithAuth(
        `${API_URL}/communities/${communityId}/posts/${postId}/replies/${replyId}/like`,
        {
            method: 'POST',
            credentials: 'include',
        }
    );

    if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to toggle like on reply');
    }

    return res.json();
}

/**
 * Create a thread in a community
 */
export async function createCommunityThread(
    communityId: string,
    data: CreateThreadDto
): Promise<Thread> {
    const res = await fetchWithAuth(`${API_URL}/communities/${communityId}/threads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(data),
    });

    if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to create thread');
    }

    const result = await res.json();
    return result.thread;
}

/**
 * Create a reply to a thread
 */
export async function createThreadReply(
    communityId: string,
    threadId: string,
    data: CreateThreadReplyDto
): Promise<ThreadReply> {
    const res = await fetchWithAuth(`${API_URL}/communities/${communityId}/threads/${threadId}/replies`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(data),
    });

    if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to create thread reply');
    }

    const result = await res.json();
    return result.reply;
}

/**
 * Get replies for a thread
 */
export async function getThreadReplies(
    communityId: string,
    threadId: string
): Promise<ThreadReply[]> {
    const res = await fetchWithAuth(`${API_URL}/communities/${communityId}/threads/${threadId}/replies`, {
        credentials: 'include',
    });

    if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to fetch thread replies');
    }

    const data: ThreadReplyListResponse = await res.json();
    return data.replies;
}

/**
 * Get chat messages for a community
 */
export async function getCommunityChatMessages(communityId: string): Promise<ChatMessage[]> {
    const res = await fetchWithAuth(`${API_URL}/communities/${communityId}/chat`, {
        credentials: 'include',
    });

    if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to fetch chat messages');
    }

    const data: ChatMessageListResponse = await res.json();
    return data.messages;
}

/**
 * Create a chat message in a community
 */
export async function createCommunityChatMessage(
    communityId: string,
    data: CreateChatMessageDto
): Promise<ChatMessage> {
    const res = await fetchWithAuth(`${API_URL}/communities/${communityId}/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(data),
    });

    if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to create chat message');
    }

    const result = await res.json();
    return result.message;
}

// ===== PROFILE =====

/**
 * Get user profile
 */
export async function getProfile(userId: string): Promise<ProfileResponse> {
    const res = await fetchWithAuth(`${API_URL}/profile/${userId}`, {
        credentials: 'include',
    });

    if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to fetch profile');
    }

    return res.json();
}

/**
 * Update user profile
 */
export async function updateProfile(
    userId: string,
    data: UpdateProfileDto
): Promise<UpdateProfileResponse> {
    const res = await fetchWithAuth(`${API_URL}/profile/${userId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(data),
    });

    if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to update profile');
    }

    return res.json();
}

/**
 * Pin or unpin a post
 */
export async function togglePinPost(
    postId: number,
    data: PinPostDto
): Promise<PinPostResponse> {
    const res = await fetchWithAuth(`${API_URL}/profile/${postId}/pin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(data),
    });

    if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to toggle pin');
    }

    return res.json();
}

/**
 * Create an admin post on a user's profile
 */
export async function createAdminPost(
    userId: string,
    data: CreateAdminPostDto
): Promise<AdminPostResponse> {
    const res = await fetchWithAuth(`${API_URL}/profile/${userId}/admin-post`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(data),
    });

    if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to create admin post');
    }

    return res.json();
}

/**
 * Get friends (mutual follows)
 */
export async function getFriends(): Promise<any[]> {
    const res = await fetchWithAuth(`${API_URL}/profile/friends`, {
        credentials: 'include',
    });

    if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to fetch friends');
    }

    const data = await res.json();
    return data.friends;
}

/**
 * Get following (users current user follows)
 */
export async function getFollowing(): Promise<any[]> {
    const res = await fetchWithAuth(`${API_URL}/profile/following`, {
        credentials: 'include',
    });

    if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to fetch following');
    }

    const data = await res.json();
    return data.following;
}

/**
 * Get followers (users that follow current user)
 */
export async function getFollowers(): Promise<any[]> {
    const res = await fetchWithAuth(`${API_URL}/profile/followers`, {
        credentials: 'include',
    });

    if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to fetch followers');
    }

    const data = await res.json();
    return data.followers;
}

/**
 * Follow a user
 */
export async function followUser(userId: string): Promise<{success: boolean, message: string}> {
    const res = await fetchWithAuth(`${API_URL}/profile/${userId}/follow`, {
        method: 'POST',
        credentials: 'include',
    });

    if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to follow user');
    }

    return res.json();
}

/**
 * Unfollow a user
 */
export async function unfollowUser(userId: string): Promise<{success: boolean, message: string}> {
    const res = await fetchWithAuth(`${API_URL}/profile/${userId}/follow`, {
        method: 'DELETE',
        credentials: 'include',
    });

    if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to unfollow user');
    }

    return res.json();
}

/**
 * Set profile theme
 */
export async function setProfileTheme(data: SetProfileThemeDto): Promise<SetThemeResponse> {
    const res = await fetchWithAuth(`${API_URL}/profile/theme`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(data),
    });

    if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to set profile theme');
    }

    return res.json();
}

// ===== REPORTS =====

export async function createReport(data: { postId?: number; communityPostId?: string; commentId?: string; reason: string }): Promise<any> {
    const res = await fetchWithAuth(`${API_URL}/reports`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(data),
    });

    if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to create report');
    }

    return res.json();
}

export async function getReports(params?: { status?: string; limit?: number; offset?: number }): Promise<{ reports: any[]; total: number }> {
    const searchParams = new URLSearchParams();
    if (params?.status) searchParams.set('status', params.status);
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.offset) searchParams.set('offset', String(params.offset));

    const res = await fetchWithAuth(`${API_URL}/reports?${searchParams.toString()}`, {
        credentials: 'include',
    });

    if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to fetch reports');
    }

    return res.json();
}

export async function updateReport(id: string, data: { status: string; reviewNote?: string }): Promise<any> {
    const res = await fetchWithAuth(`${API_URL}/reports/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(data),
    });

    if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to update report');
    }

    return res.json();
}
